import { useContext, useState } from "react";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaStartDatoFilterSetting,
} from "../../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../../accordion/Accordion";
import { dateQueries } from "../../../../../config/filter/CustomFilterConfig";
import { DatePicker, useToaster, Notification } from "rsuite";
import { isCompleteDate } from "../../../../formcontrols/datePickerValue";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";
import { ComboBox } from "../../../../filter/ComboBox";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import { format } from "date-fns";
import WarningIcon from "../../../../icons/WarningIcon";
import { f } from "../../../../../util/f";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "startdato";

export default function FirmaStartDatoFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // find active settings
  const settings: FirmaStartDatoFilterSetting | undefined = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // react things
  const [selectedStartDateQuery, setSelectedStartDateQuery] = useState(
    dateQueries[0],
  );
  const [isStartDateBetween, setIsStartDateBetween] = useState<boolean>(false);
  const [selectedStartDateFrom, setSelectedStartDateFrom] =
    useState<Date | null>(new Date());
  const [selectedStartDateTo, setSelectedStartDateTo] = useState<Date | null>(
    new Date(),
  );

  // just toasting
  const toaster = useToaster();

  // helper
  function buildFilter(settings: FirmaStartDatoFilterSetting) {
    return f<FirmaDtoFilterInput>()
      .addFilter("periode", {
        or: settings.map((set) => set.filter),
      })
      .build();
  }

  function toastFilterExists() {
    toaster.push(
      <Notification>
        <div className="d-flex flex-row gap-3 align-items-center">
          <span className={`c-theme-icon text-light bg-warning`}>
            <WarningIcon />
          </span>
          Filteret er allerede tilføjet.
        </div>
      </Notification>,
      {
        placement: "bottomEnd",
        duration: 2500,
      },
    );
  }

  // handling handlers
  function updateFilterHandler() {
    const updatedDateSettings: FirmaStartDatoFilterSetting = settings || [];

    // OK is disabled while the date is unfinished; the to-date is only
    // read when "between" is chosen (ADV-320).
    if (!selectedStartDateFrom) return;

    const selectedStartDateFromFormattedPretty = format(
      selectedStartDateFrom,
      "dd-MM-yyyy",
    );
    const selectedStartDateFromFormattedFilter = format(
      selectedStartDateFrom,
      "yyyy-MM-dd",
    );
    const selectedStartDateToFormattedPretty = format(
      selectedStartDateTo ?? selectedStartDateFrom,
      "dd-MM-yyyy",
    );
    const selectedStartDateToFormattedFilter = format(
      selectedStartDateTo ?? selectedStartDateFrom,
      "yyyy-MM-dd",
    );

    let startDateItem: FirmaStartDatoFilterSetting[number];

    if (isStartDateBetween) {
      startDateItem = {
        key: `gt-${selectedStartDateFromFormattedPretty}-lt-${selectedStartDateToFormattedPretty}`,
        displayName: `${selectedStartDateFromFormattedPretty} - ${selectedStartDateToFormattedPretty}`,
        selectedQueryName: "Mellem",
        filter: {
          and: [
            {
              fraDato: {
                gt: selectedStartDateFromFormattedFilter,
              },
            },
            {
              fraDato: {
                lt: selectedStartDateToFormattedFilter,
              },
            },
          ],
        },
      };
    } else {
      startDateItem = {
        key: `${selectedStartDateQuery.query}-${selectedStartDateFromFormattedPretty}`,
        displayName: selectedStartDateFromFormattedPretty,
        selectedQueryName: selectedStartDateQuery.displayName,
        filter: {
          fraDato: {
            [selectedStartDateQuery.query]:
              selectedStartDateFromFormattedFilter,
          },
        },
      };
    }

    const filterExists = updatedDateSettings.some(
      (item) => item.key === startDateItem.key,
    );

    if (filterExists) {
      toastFilterExists();
    } else {
      updatedDateSettings.push(startDateItem);
    }

    const canBuildFilter = updatedDateSettings.length;
    const filterString = canBuildFilter ? buildFilter(updatedDateSettings) : "";

    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaStartDatoFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedDateSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(key: string) {
    const updatedSettings = settings?.filter((set) => set.key !== key);
    const canBuildFilter = updatedSettings?.length;

    if (canBuildFilter) {
      const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";
      const queryFilter: QueryFilter<
        FirmaFilterKeys,
        FirmaStartDatoFilterSetting
      > = {
        filter: filterString,
        filterKey: filterKey,
        settings: updatedSettings,
        parentFilter: "and",
      };
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Startdato"
      value="startdato"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <div className="c-applied-filter-group">
        {settings?.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.selectedQueryName}`}
            label={`${set.displayName}`}
            key={`${set.key}`}
            removeFilterHandler={() => {
              removeFilterHandler(set.key);
            }}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedStartDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsStartDateBetween(true);
              return;
            } else {
              setIsStartDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedStartDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedStartDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedStartDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isStartDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedStartDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedStartDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedStartDateFrom ||
            (isStartDateBetween &&
              (!selectedStartDateTo ||
                selectedStartDateFrom > selectedStartDateTo))
          }
          onClick={() => {
            updateFilterHandler();
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
